import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Badge } from '@vaiu/ui/badge';
import { 
  Shield, 
  ShoppingBag, CheckCircle2, Loader2, Smartphone, CheckCircle, Building2, AlertCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi'
import { currencyService } from '@/services/currencyService';;
import { LoadingSpinner } from '@vaiu/ui';
import CreateCashToken from '@/components/CreateCashToken';
import { useShoppingCart } from '@/hooks/useShoppingCart';
import { CartSummary } from '@/components/shopping/CartSummary';
import { PaymentConfirmationQR } from '@/components/PaymentConfirmationQR';
import TokenCard from '@/components/TokenCard';
import { i18n } from '@/utils/i18n';
import { getMerchantCurrency } from '@/utils/currencyDetection';
import QRCode from 'react-qr-code';
import { useIsMobile } from '@/hooks/use-mobile';
import GuestCheckoutChoice from '@/components/payment/GuestCheckoutChoice';
import { CurrencyDisplay } from '@vaiu/ui';
import { onTokenCreated, TokenCreatedDetail } from '@/utils/lightEventEmitter';
import PublicProfileCard from '@/components/profile/PublicProfileCard';
import { sanitizeAmount } from '@vaiu/utils/currency';
import { preloadStripeJs } from '@/config/stripe';

/**
 * PayPage - Payment page for creating tokens and processing payments
 * 
 * Benefits:
 * - Uses CreateCashToken component for token creation with consistent UX
 * - Supports guest checkout and authenticated user flows
 * - Apple Pay/Google Pay automatically available via CreateCashToken
 * - Less code duplication by reusing CreateCashToken component
 * - Consistent with guest checkout experience
 */
const PayPage: React.FC = () => {
  // Helper function to replace template variables in translations
  // Moved inside component to avoid module-level initialization issues
  const t = (key: string, replacements?: Record<string, string>) => {
    let translation = i18n.__(key as any);
    if (replacements) {
      Object.entries(replacements).forEach(([placeholder, value]) => {
        translation = translation.replace(new RegExp(`{{${placeholder}}}`, 'g'), value);
      });
    }
    return translation;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const { merchantId: merchantIdParam } = useParams<{ merchantId?: string }>();
  const { user, isGuest, login } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Warm up Stripe.js on /pay only (not app-wide)
  useEffect(() => {
    preloadStripeJs();
  }, []);

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const payeeIdParam = queryParams.get('payeeId'); // New generic parameter for any payee
  const autoDepositParam = queryParams.get('autoDeposit'); // userId to auto-deposit to
  const merchantIdQuery = queryParams.get('merchantId'); // Support old query param pattern
  const recipientIdParam = queryParams.get('recipientId'); // Alternative parameter name
  
  // Determine the actual payee/recipient userId from various sources
  // Priority: URL path param > payeeId > recipientId > autoDeposit > merchantId (legacy)
  const payeeId = merchantIdParam || payeeIdParam || recipientIdParam || autoDepositParam || merchantIdQuery;
  const merchantId = payeeId; // Keep merchantId variable for backward compatibility internally
  
  console.log('🔍 Payment route params:', {
    payeeIdParam,
    recipientIdParam,
    merchantIdParam,
    autoDepositParam,
    merchantIdQuery,
    payeeId,
    fullPath: location.pathname,
    fullSearch: location.search,
    allQueryParams: Object.fromEntries(queryParams.entries())
  });
  
  const queryAmountRaw = queryParams.get('amount');
  const queryCurrency = queryParams.get('currency')?.toUpperCase() || 'USD';
  const queryMessage = queryParams.get('message') || '';
  // Auto-pay logic:
  // - autoPay=true → enable auto-pay
  // - If not → default to manual mode (no auto-pay)
  // const noPay = queryParams.get('noPay') === 'true';
  const autoPay = queryParams.get('autoPay') === 'true';
  const postToParent = queryParams.get('postToParent') === 'true' || queryParams.get('ptp') === 'true';
  const embedMode = queryParams.get('embed') === 'true';

  // Sanitize and validate amount from query parameter
  // Use maxDecimals=8 to handle high precision currencies (e.g., BTC, ETH)
  // Don't allow negative amounts for payments
  const sanitizedQueryAmount = sanitizeAmount(queryAmountRaw, 8, false);
  const queryAmount = sanitizedQueryAmount !== null ? sanitizedQueryAmount.toString() : '';

  // State management
  const [loading, setLoading] = useState(true);
  const [payeeInfo, setPayeeInfo] = useState<any>(null);
  const [payeeError, setPayeeError] = useState(false);
  const [payeeType, setPayeeType] = useState<'merchant' | 'user' | null>(null);
  const [createdToken, setCreatedToken] = useState<any>(null);
  const [detectedCurrency, setDetectedCurrency] = useState<string | null>(null);
  const [guestCheckoutChoice, setGuestCheckoutChoice] = useState<'guest' | 'signin' | null>(null);
  const [guestCheckoutInProgress, setGuestCheckoutInProgress] = useState(false);
  const [userBalances, setUserBalances] = useState<Record<string, number>>({});
  const [selectedCurrency, setSelectedCurrency] = useState<string>(queryCurrency);
  
  // Note: postToParent messaging is now handled in CreateCashToken component
  // This ref is no longer needed but kept for backward compatibility if needed
  const messageSentRef = useRef(false);
  
  // Refs to access CreateCashToken's functions
  const createCashTokenCleanupRef = useRef<(() => void) | null>(null);
  const createCashTokenResetRef = useRef<(() => void) | null>(null);

  // Shopping cart integration
  const { 
    items: cartItems, 
    getTotalAmount, 
    clearCart,
    currency: cartCurrency 
  } = useShoppingCart(merchantId);
  
  const hasCart = cartItems.length > 0 && merchantId;
  const cartTotal = getTotalAmount();
  const currencyLockedByCheckout = Boolean(queryParams.get('currency')) || hasCart;

  // Determine payment amount - use sanitized amount
  const paymentAmount = hasCart 
    ? cartTotal 
    : sanitizedQueryAmount !== null
      ? sanitizedQueryAmount
      : 0;
  
  // Smart currency selection: selectedCurrency state > query param > detected merchant currency > cart > user > fallback
  const paymentCurrency = selectedCurrency || (hasCart 
    ? cartCurrency 
    : (queryParams.get('currency') ? queryCurrency : (detectedCurrency || user?.currency || 'USD')));
  
  // Update selectedCurrency when query param or detected currency changes (only if not already set)
  useEffect(() => {
    if (!selectedCurrency) {
      const initialCurrency = hasCart 
        ? cartCurrency 
        : (queryParams.get('currency') ? queryCurrency : (detectedCurrency || user?.currency || 'USD'));
      setSelectedCurrency(initialCurrency);
    }
  }, [hasCart, cartCurrency, queryCurrency, detectedCurrency, user?.currency]);

  // Load payee info - wrapped in useCallback to fix dependency issues
  const loadPayeeInfo = useCallback(async () => {
    console.log('🔍 loadPayeeInfo called with payeeId:', payeeId);
    setLoading(true);
    setPayeeError(false);
    
    // Recreate queryParams from location.search inside callback
    const currentQueryParams = new URLSearchParams(location.search);
    
    // Helper to check if a value is a valid string (not an object)
    const isValidString = (value: any): boolean => {
      return value !== null && value !== undefined && typeof value === 'string' && value.trim() !== '';
    };
    
    // Helper to check if an object is a valid address object with actual string values
    const isValidAddressObject = (addr: any): boolean => {
      if (!addr) return false;
      if (typeof addr === 'string' && addr.trim() !== '') return true;
      if (typeof addr !== 'object') return false;
      if ('$concat' in addr || '$add' in addr || '$multiply' in addr || '$lookup' in addr) return false;
      const hasValidStreet = isValidString(addr.street) || isValidString(addr.address);
      const hasValidCity = isValidString(addr.city);
      const hasValidState = isValidString(addr.state);
      const hasValidZip = isValidString(addr.zip) || isValidString(addr.zipCode);
      const hasValidCountry = isValidString(addr.country);
      return hasValidStreet || hasValidCity || hasValidState || hasValidZip || hasValidCountry;
    };
    
    // Helper to extract valid address object from potentially invalid data
    const extractValidAddress = (addr: any): any | undefined => {
      if (!addr) return undefined;
      if (typeof addr === 'string' && addr.trim() !== '') {
        return { street: addr.trim() };
      }
      if (!isValidAddressObject(addr)) return undefined;
      const result: any = {};
      if (isValidString(addr.street)) result.street = addr.street;
      else if (isValidString(addr.address)) result.street = addr.address;
      if (isValidString(addr.city)) result.city = addr.city;
      if (isValidString(addr.state)) result.state = addr.state;
      if (isValidString(addr.zip)) result.zip = addr.zip;
      else if (isValidString(addr.zipCode)) result.zip = addr.zipCode;
      if (isValidString(addr.country)) result.country = addr.country;
      return Object.keys(result).length > 0 ? result : undefined;
    };

    const isMerchantRoleEntry = (p: any) =>
      String(p?.role ?? '')
        .toUpperCase()
        .replace(/\s+/g, '') === 'MERCHANT';

    const merchantDataFromPublicMerchantApi = (merchantProfile: any) => {
      const merchantName =
        merchantProfile.businessName ||
        merchantProfile.name ||
        merchantProfile.displayName ||
        'Unknown Business';
      const merchantType =
        typeof merchantProfile.businessType === 'string'
          ? merchantProfile.businessType
          : typeof merchantProfile.businessType === 'object' &&
              merchantProfile.businessType !== null
            ? String(merchantProfile.businessType)
            : merchantProfile.businessType || '';
      const kybStatus =
        merchantProfile.status?.kybStatus ||
        merchantProfile.verification?.kycStatus ||
        merchantProfile.kycStatus;
      const kycStatus =
        merchantProfile.status?.kycStatus ||
        merchantProfile.verification?.kycStatus ||
        merchantProfile.kycStatus;
      const isPublished =
        merchantProfile.settings?.visibility?.isPublished ??
        merchantProfile.status?.isPublished ??
        merchantProfile.isPublished ??
        false;
      return {
        id: payeeId,
        name: merchantName,
        displayName: merchantName,
        businessName: merchantName,
        businessType: merchantType,
        description: merchantProfile.description,
        logo: merchantProfile.media?.logo || merchantProfile.logo,
        photos: merchantProfile.media?.images || merchantProfile.photos || [],
        website: merchantProfile.contact?.website || merchantProfile.website,
        phone: merchantProfile.contact?.phone || merchantProfile.phone,
        email: merchantProfile.contact?.email || merchantProfile.email,
        address: extractValidAddress(
          merchantProfile.address ||
            merchantProfile.contact?.address ||
            merchantProfile.location?.components
        ),
        verification: {
          isVerified:
            merchantProfile.status?.verified ||
            merchantProfile.verification?.isVerified ||
            false,
          kycStatus:
            kycStatus || (merchantProfile.status?.verified ? 'approved' : 'pending'),
          kybStatus:
            kybStatus || (merchantProfile.status?.verified ? 'approved' : 'pending'),
        },
        isMerchant: true,
        isPublished: isPublished,
        preferredCurrency: merchantProfile.preferredCurrency,
      };
    };

    const applyPublicMerchantApiProfile = (merchantProfile: any) => {
      const merchantData = merchantDataFromPublicMerchantApi(merchantProfile);
      setPayeeInfo(merchantData);
      setPayeeType('merchant');
      if (!currentQueryParams.get('currency')) {
        const merchantCurrency = getMerchantCurrency(merchantData);
        setDetectedCurrency(merchantCurrency);
      }
    };
    
    try {
      // Use unified public user profile endpoint that returns user + role profiles
      console.log('👤 Loading public user profile for:', payeeId);
      const publicProfile = await platformApi.user.getPublicUserProfile(payeeId!);
      
      console.log('📦 Received public user profile:', publicProfile);
      
      // Check if user has MERCHANT role profile (API may omit nested profile when not "published" there)
      const merchantRoleProfile = publicProfile.public?.otherProfiles?.find(isMerchantRoleEntry);
      
      if (merchantRoleProfile && merchantRoleProfile.profile) {
        // User has merchant profile - use merchant data
        const merchantProfile = merchantRoleProfile.profile;
        const merchantName = merchantProfile.name || merchantProfile.businessName || publicProfile.username || 'Unknown Business';
        const merchantType = typeof merchantProfile.type === 'string' 
          ? merchantProfile.type 
          : (typeof merchantProfile.businessType === 'string' ? merchantProfile.businessType : '');
        const merchantLogo = merchantProfile.media?.logo || merchantProfile.logo;
        const merchantPhotos = merchantProfile.media?.images || merchantProfile.photos || [];
        const merchantWebsite = merchantProfile.contact?.website || merchantProfile.website;
        const merchantPhone = merchantProfile.contact?.phone || merchantProfile.contact?.mobile || merchantProfile.phone;
        const merchantEmail = merchantProfile.contact?.email || merchantProfile.email;
        const merchantVerified = merchantProfile.status?.verified || merchantProfile.verification?.isVerified || false;
        
        // Extract address from merchant profile
        let merchantAddress: any = undefined;
        if (merchantProfile.contact?.address) {
          merchantAddress = extractValidAddress(merchantProfile.contact.address);
        }
        if (!merchantAddress && merchantProfile.location?.components) {
          merchantAddress = extractValidAddress(merchantProfile.location.components);
          if (merchantAddress && merchantProfile.location.components.postalCode && !merchantAddress.zip) {
            merchantAddress.zip = merchantProfile.location.components.postalCode;
          }
        }
        
        // Extract KYB/KYC status from merchant profile
        const kybStatus = merchantProfile.status?.kybStatus || merchantProfile.verification?.kycStatus || merchantProfile.kycStatus;
        const kycStatus = merchantProfile.status?.kycStatus || merchantProfile.verification?.kycStatus || merchantProfile.kycStatus;
        const isPublished = merchantProfile.settings?.visibility?.isPublished ?? merchantProfile.status?.isPublished ?? false;
        
        const merchantData = {
          id: payeeId,
          name: merchantName,
          displayName: merchantName,
          businessName: merchantName,
          businessType: merchantType,
          description: merchantProfile.description,
          logo: merchantLogo,
          photos: merchantPhotos,
          website: merchantWebsite,
          phone: merchantPhone,
          email: merchantEmail,
          address: merchantAddress,
          verification: { 
            isVerified: merchantVerified,
            kycStatus: kycStatus || (merchantVerified ? 'approved' : 'pending'),
            kybStatus: kybStatus || (merchantVerified ? 'approved' : 'pending')
          },
          isMerchant: true,
          isPublished: isPublished,
          location: merchantProfile.location,
          preferredCurrency: merchantProfile.settings?.currency?.preferred || merchantProfile.preferredCurrency
        };
        
        setPayeeInfo(merchantData);
        setPayeeType('merchant');
        
        // Detect merchant's currency if not explicitly set in query
        if (!currentQueryParams.get('currency')) {
          const merchantCurrency = getMerchantCurrency(merchantData);
          setDetectedCurrency(merchantCurrency);
          console.log('💱 Detected merchant currency:', merchantCurrency);
        }
        
        console.log('✅ Merchant profile loaded from user profile:', merchantName);
      } else {
        // User profile often omits embedded MERCHANT payload even when payee is a merchant.
        // Public merchant endpoint uses the same id (user / merchant owner id) for pay links.
        let loadedFromMerchantApi = false;
        try {
          console.log(
            '🏪 No embedded merchant on user profile; trying GET /public/merchant/:id'
          );
          const mp = await platformApi.merchant.getPublicMerchantProfile(payeeId!);
          applyPublicMerchantApiProfile(mp);
          loadedFromMerchantApi = true;
          console.log(
            '✅ Merchant profile loaded via public merchant endpoint:',
            mp.businessName || mp.name
          );
        } catch (merchantPublicErr) {
          console.debug(
            'PayPage: public merchant profile not available for payee (ok for non-merchants):',
            merchantPublicErr
          );
        }

        if (!loadedFromMerchantApi) {
          // Regular user without merchant profile (or merchant public profile unavailable)
          const displayName = publicProfile.username || `User ${payeeId!.slice(-6)}`;
          const userData = {
            id: payeeId,
            name: displayName,
            displayName: displayName,
            username: publicProfile.username,
            email: undefined,
            phone: undefined,
            avatar: undefined,
            isMerchant: false,
            verified: false,
            hasMerchantRole:
              publicProfile.public?.otherProfiles?.some(isMerchantRoleEntry) || false,
          };

          setPayeeInfo(userData);
          setPayeeType('user');
          console.log(
            '✅ User profile loaded (non-merchant or no public merchant payload):',
            userData.displayName
          );
        }
      }
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      const msg = err.message ?? '';

      // These are common when /user/profile returns HTML (SPA/proxy) or empty body; merchant endpoint often still works.
      const recoverableUserProfileFailure =
        msg.includes('USER_PROFILE_HTML_RESPONSE') ||
        msg.includes('USER_PROFILE_INVALID_JSON') ||
        msg.includes('USER_PROFILE_EMPTY_BODY') ||
        /\b404\b/.test(msg) ||
        msg.toLowerCase().includes('not found');

      if (recoverableUserProfileFailure) {
        console.debug(
          '[PayPage] Public user profile unavailable; using merchant profile if available.',
          msg
        );
      } else {
        console.error('❌ Failed to load payee info:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          payeeId,
        });
      }

      try {
        const merchantProfile = await platformApi.merchant.getPublicMerchantProfile(payeeId!);
        applyPublicMerchantApiProfile(merchantProfile);
        if (import.meta.env.DEV && recoverableUserProfileFailure) {
          console.debug(
            '[PayPage] Merchant profile loaded after user profile failure:',
            merchantProfile.businessName || merchantProfile.name
          );
        }
        return;
      } catch (fallbackError: unknown) {
        console.error('❌ Payee profiles failed (public user + public merchant):', {
          payeeId,
          userProfileError: msg,
          merchantProfileError:
            fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
        });
      }
      
      // Even if profile loading fails, we can still show basic info and allow payment
      // Set minimal payee info so user can proceed with payment
      // Name must come from server lookup (security: never trust URL params)
      const fallbackName = `User ${payeeId.slice(-8)}`;
      setPayeeInfo({
        id: payeeId,
        name: fallbackName,
        displayName: fallbackName,
        isMerchant: false,
        verified: false
      });
      setPayeeType('user');
      
      // User profile "missing" (404/not found) is expected for some payees; skip toast.
      const showProfileToast =
        !msg.includes('404') && !msg.toLowerCase().includes('not found');
      if (showProfileToast) {
        toast({
          title: 'Profile Information Unavailable',
          description: 'Limited profile information available. You can still proceed with payment.',
          variant: 'default',
        });
      }
      
      // Don't set payeeError to true - we want to show what we know, not an error state
      setPayeeError(false);
    } finally {
      console.log('🏁 Setting loading to false');
      setLoading(false);
    }
  }, [payeeId, location.search, toast, getMerchantCurrency]);

  // Load payee info
  // Fetch user balances for CreateCashToken (which needs balances for business logic)
  // Note: BalancesSelector now fetches its own balances independently
  // Note: CreateCashToken listens to balance:update events independently and fetches fresh balances itself
  useEffect(() => {
    if (!isGuest && user) {
      const fetchBalances = async () => {
        try {
          const balances = await platformApi.getBalances();
          setUserBalances(balances || {});
        } catch (error) {
          console.error('Failed to fetch balances:', error);
          // Fallback to user.balances if API fails
          setUserBalances(user.balances || {});
        }
      };
      fetchBalances();
    }
  }, [isGuest, user]);

  useEffect(() => {
    console.log('🎯 Payee loading useEffect triggered. payeeId:', payeeId);
    if (payeeId) {
      console.log('✅ PayeeId exists, calling loadPayeeInfo');
      loadPayeeInfo();
    } else {
      console.log('❌ No payeeId, setting loading to false');
      setLoading(false);
    }
  }, [payeeId, loadPayeeInfo]);
  
  // Listen for token:created event from CreateCashToken
  // This replaces the direct postToParent handling - CreateCashToken now handles parent messaging
  // Note: BalancesSelector now manages its own balance updates internally, so we don't need to update userBalances here
  useEffect(() => {
    console.log('🔌 [PayPage] Setting up token:created event listener');
    const unsubscribeTokenCreated = onTokenCreated((detail: TokenCreatedDetail) => {
      console.log('🎫 [PayPage] Token created event received:', detail);
      
      // Note: BalancesSelector listens to token:created and balance:update events independently
      // and updates its own internal balance state, so we don't need to update userBalances here
      
    });
    
    return () => {
      console.log('🔌 [PayPage] Cleaning up token:created listener');
      unsubscribeTokenCreated();
    };
  }, []);

  // Handle guest checkout choice - Step 1 of Guest Checkout PRD
  const handleContinueAsGuest = async () => {
    try {
      setGuestCheckoutInProgress(true);
      console.log('🌐 GUEST CHECKOUT: User chose to continue as guest');
      
      // Create guest session - this creates a real guest user with userId
      const result = await platformApi.guestLogin();
      
      if (result && result.token) {
        console.log('✅ GUEST CHECKOUT: Guest userId created:', result.userId);
        
        // Log in as guest (this happens in AuthContext)
        await login(result.token, true, {
          username: result.username,
          userId: result.userId,
          sessionExpiry: result.sessionExpiry
        });
        
        // Proceed to guest checkout (Step 2)
        setGuestCheckoutChoice('guest');
        console.log('✅ GUEST CHECKOUT: Ready for guest checkout flow');
      }
    } catch (error) {
      console.error('❌ GUEST CHECKOUT: Failed to create guest session:', error);
      toast({
        title: 'Error',
        description: 'Failed to start guest checkout. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setGuestCheckoutInProgress(false);
    }
  };

  // Handle payment success (currently unused - kept for potential future use)
  const handlePaymentSuccess = async (result: { success: boolean; method: string; message?: string; transactionId?: string; tokenKey?: string }) => {
    try {
      toast({
        title: 'Payment Successful!',
        description: result.message || 'Your payment has been processed.',
      });

      // Clear cart if applicable
      if (hasCart) {
        clearCart();
      }

      // Navigate to success page or dashboard
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete payment',
        variant: 'destructive'
      });
    }
  };

  // Handle token creation - memoized to prevent re-renders
  const handleTokenCreated = useCallback((token: any) => {
    console.log('🎯 Token created with response:', token);
    
    // Check if this was an auto-deposit scenario
    if (token.recipientDelivery && payeeId) {
      // Don't show the token for auto-deposits, show confirmation instead
      if (token.recipientDelivery.status === 'auto_deposited') {
        toast({
          title: t('vaiu.payment.toast_payment_delivered_successfully'),
          description: t('vaiu.payment.toast_payment_delivered_successfully_desc', {
            amount: token.amount,
            currency: token.currency,
            name: payeeInfo?.displayName || payeeInfo?.name || 'the recipient'
          }),
          variant: 'default'
        });
        // Don't set createdToken - we don't want to show the token card
        // Instead, maybe redirect or show a success state
        setCreatedToken({
          ...token,
          isAutoDeposited: true,
          recipientName: payeeInfo?.displayName || payeeInfo?.name || 'recipient'
        });
      } else if (token.recipientDelivery.status === 'in_inbox') {
        toast({
          title: t('vaiu.payment.toast_payment_to_inbox'),
          description: t('vaiu.payment.toast_payment_to_inbox_desc', {
            name: payeeInfo?.displayName || payeeInfo?.name || 'the recipient'
          }),
          variant: 'default'
        });
        // Show limited token info for inbox delivery
        setCreatedToken({
          ...token,
          isInInbox: true,
          recipientName: payeeInfo?.displayName || payeeInfo?.name || 'recipient'
        });
      } else if (token.recipientDelivery.status === 'recipient_not_found') {
        toast({
          title: t('vaiu.payment.toast_recipient_not_found'),
          description: t('vaiu.payment.toast_recipient_not_found_desc'),
          variant: 'destructive'
        });
        // Show the full token since it couldn't be delivered
        setCreatedToken(token);
      }
    } else if (payeeId) {
      // Payment to payee but no auto-deposit response (treat as successful payment)
      setCreatedToken({
        ...token,
        isPayment: true,
        recipientName: payeeInfo?.displayName || payeeInfo?.name || 'recipient'
      });
      toast({
        title: t('vaiu.payment.toast_payment_delivered'),
        description: t('vaiu.payment.toast_payment_delivered_desc', {
          name: payeeInfo?.displayName || payeeInfo?.name || 'the recipient'
        }),
      });
    } else {
      // Regular token creation without payee
      setCreatedToken(token);
      toast({
        title: t('vaiu.payment.toast_token_created'),
        description: t('vaiu.payment.toast_token_created_desc', { key: token.key }),
      });
    }
  }, [payeeId, payeeInfo, toast]);

  // Helper function to clear token
  // NOTE: PayPal param cleanup is now handled internally by CreateCashToken component
  // When token is cleared, trigger PayPal cleanup and reset token creation state
  const handleClearToken = useCallback(() => {
    setCreatedToken(null);
    
    // CRITICAL: Trigger PayPal cleanup when token card is closed
    // This prevents PayPal params from triggering another token creation attempt
    if (createCashTokenCleanupRef.current) {
      console.log('🧹 [PayPage] Triggering PayPal cleanup after token card closed');
      createCashTokenCleanupRef.current();
    }
    
    // CRITICAL: Reset token creation state to allow creating a new token
    // This ensures user can create another token after closing the previous one
    if (createCashTokenResetRef.current) {
      console.log('🔄 [PayPage] Resetting token creation state to allow new token');
      createCashTokenResetRef.current();
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <LoadingSpinner text="Loading payment details..." />
      </div>
    );
  }

  // Step 1 of Guest Checkout PRD: Show authentication choice screen if user is not logged in
  // Only show if we haven't already made a choice and the user is not authenticated
  if (!user && !guestCheckoutChoice) {
    return (
      <GuestCheckoutChoice
        payeeName={payeeInfo?.displayName || payeeInfo?.name || payeeInfo?.businessName}
        payeeType={payeeType || 'merchant'}
        onContinueAsGuest={handleContinueAsGuest}
        loading={guestCheckoutInProgress}
      />
    );
  }

  return (
      <div className={embedMode ? "min-h-screen w-full max-w-full min-w-0 overflow-x-hidden bg-white p-2 sm:p-4" : "min-h-screen w-full max-w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-2 sm:p-4"}>
      <div className="mx-auto w-full max-w-full min-w-0">

        <div className="grid w-full min-w-0 max-w-full gap-3 sm:max-w-4xl sm:mx-auto lg:grid-cols-3">
          {/* Left Column: Merchant/Payment Info */}
          <div className="min-w-0 space-y-4 lg:col-span-2">
            {/* Payee Info Card - Show loading, error, or info */}
            {payeeId && !payeeInfo && !payeeError && (
              <Card>
                <CardContent className="py-4">
                  <div className="text-center text-muted-foreground flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">{t('common.loadingProfile')}</span>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {payeeInfo && (
              <PublicProfileCard 
                profile={payeeInfo}
                paymentCurrency={paymentCurrency}
              />
            )}

            {/* Shopping Cart Summary */}
            {hasCart && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5" />
                    {t('pay.orderSummary')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CartSummary 
                    showActions={false}
                  />
                </CardContent>
              </Card>
            )}

            {/* Payment/Token Creation */}
            {/* Always render CreateCashToken to keep balance:update listener active */}
            {/* Hide container div when token is created, but component stays mounted for event handling */}
            <div style={{ display: createdToken ? 'none' : 'block' }}>
              <CreateCashToken
                  key={`pay-ct${location.pathname}${location.search}`}
                  typeOfToken="cash"
                  selectedCurrency={selectedCurrency || paymentCurrency || user?.currency || 'USD'}
                  balances={userBalances || user?.balances || {}}
                  allowCurrencyChange={currencyLockedByCheckout ? false : undefined}
                  initialAmount={queryAmount || ''}
                  initialMessage={queryMessage || (payeeId ? `Payment to ${payeeInfo?.displayName || payeeInfo?.name || 'recipient'}` : '')}
                  merchantId={payeeId}  // Pass payeeId for auto-deposit
                  payeeName={payeeInfo?.displayName || payeeInfo?.businessName || payeeInfo?.name}  // Pass payee name for button text
                  autoSubmit={autoPay && sanitizedQueryAmount !== null && sanitizedQueryAmount > 0}  // Auto-submit if autoPay and valid sanitized amount provided
                  onSubmit={handleTokenCreated}
                  embedMode={embedMode}
                  isGuest={isGuest}
                  postToParent={postToParent}  // Pass postToParent prop to CreateCashToken
                  showTokenAfterCreation={false}  // PayPage has custom UI for token display (auto-deposit, inbox, etc.)
                  cleanupPayPalParamsRef={createCashTokenCleanupRef}  // Expose cleanup function so PayPage can trigger it
                  resetTokenCreationRef={createCashTokenResetRef}  // Expose reset function so PayPage can reset state
              />
            </div>

            {/* Payment Success or Token Display */}
            {createdToken && (
              <Card className="animate-in slide-in-from-bottom duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      {payeeId ? t('pay.paymentComplete') : 
                       createdToken.isAutoDeposited ? t('pay.paymentDeliveredSuccessfully') : 
                       createdToken.isInInbox ? t('pay.paymentDeliveredToInbox') :
                       createdToken.type === 'invoice' ? t('pay.recentlyCreatedInvoice') : t('pay.recentlyCreatedToken')}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearToken}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ×
                    </Button>
                  </div>
                  <CardDescription>
                    {payeeId && !createdToken.recipientDelivery ? 
                      t('pay.paymentDeliveredTo', { name: payeeInfo?.displayName || payeeInfo?.name || t('common.recipient') }) :
                     createdToken.isAutoDeposited ? 
                      t('pay.yourPaymentDelivered', { amount: createdToken.amount, currency: createdToken.currency, name: createdToken.recipientName }) :
                     createdToken.isInInbox ? 
                      t('pay.recipientWillReview', { name: createdToken.recipientName }) :
                      createdToken.type === 'invoice' ? t('pay.invoiceCreatedSuccessfully') : t('pay.tokenCreatedSuccessfully')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Show success UI for payee payments, TokenCard for general token creation */}
                  {payeeId && createdToken.isAutoDeposited ? (
                    // For auto-deposited payments, show enhanced confirmation with QR
                    <PaymentConfirmationQR
                      amount={createdToken.amount}
                      currency={createdToken.currency}
                      merchantName={payeeInfo?.displayName || payeeInfo?.name || 'Merchant'}
                      tokenKey={createdToken.key || ''}
                      transactionId={createdToken.recipientDelivery?.transactionId}
                      timestamp={createdToken.createdAt || new Date().toISOString()}
                      merchantId={payeeId}
                      onNewPayment={handleClearToken}
                      onGoBack={() => navigate(-1)}
                    />
                  ) : payeeId ? (
                    // Payment to specific payee - show success confirmation
                    <div className="space-y-4">
                      <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-green-900 dark:text-green-100">
                              {t('pay.paymentSuccessful')}
                            </p>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                              {t('pay.yourPaymentDelivered', {
                                amount: createdToken.amount,
                                currency: createdToken.currency,
                                name: payeeInfo?.displayName || payeeInfo?.name || t('common.recipient')
                              })}
                            </p>
                            {createdToken.key && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                                {t('common.reference')}: {createdToken.key}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex gap-3">
                        <Button
                          onClick={handleClearToken}
                          variant="default"
                        >
                          {t('pay.makeAnotherPayment')}
                        </Button>
                        <Button
                          onClick={() => navigate(-1)}
                          variant="outline"
                        >
                          {t('common.goBack')}
                        </Button>
                      </div>
                    </div>
                  ) : !createdToken.isAutoDeposited ? (
                    createdToken.isInInbox ? (
                      // For inbox delivery, show simplified confirmation
                      <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-blue-900 dark:text-blue-100">{t('pay.paymentInInbox')}</p>
                              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                {t('pay.recipientHasControl', { name: createdToken.recipientName })}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="border-t pt-4">
                          <dl className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <dt className="text-muted-foreground">{t('common.amount')}</dt>
                              <dd className="font-medium">{createdToken.amount} {createdToken.currency}</dd>
                            </div>
                            <div>
                              <dt className="text-muted-foreground">{t('common.recipient')}</dt>
                              <dd className="font-medium">{createdToken.recipientName}</dd>
                            </div>
                            {createdToken.message && (
                              <div className="col-span-2">
                                <dt className="text-muted-foreground">{t('common.message')}</dt>
                                <dd className="font-medium">{createdToken.message}</dd>
                              </div>
                            )}
                          </dl>
                        </div>
                      </div>
                    ) : (
                      // Regular token display for general mint (no payee)
                      <TokenCard 
                        token={createdToken} 
                        theme="default"
                        showActions={true}
                        showQRCode={true}
                        showPrepaidCardOption={true}
                        className="shadow-none border-0"
                        borderless={true}
                      />
                    )
                  ) : (
                    // For auto-deposited payments, show success confirmation
                    <div className="space-y-4">
                      <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-green-900 dark:text-green-100">{t('pay.paymentDeposited')}</p>
                            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                              {t('pay.yourPaymentDepositedTo', {
                                amount: createdToken.amount,
                                currency: createdToken.currency,
                                name: createdToken.recipientName
                              })}
                            </p>
                            {createdToken.recipientDelivery?.transactionId && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                                {t('common.transactionId')}: {createdToken.recipientDelivery.transactionId}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      {createdToken.message && (
                        <div className="border-t pt-4">
                          <p className="text-sm text-muted-foreground">{t('pay.messageSentWithPayment')}:</p>
                          <p className="text-sm font-medium mt-1">{createdToken.message}</p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column: Help/Info */}
          <div className="min-w-0 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('pay.security.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('pay.security.bankLevelEncryption')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('pay.security.bankLevelEncryptionDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('pay.security.multiplePaymentOptions')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('pay.security.multiplePaymentOptionsDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Building2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('pay.security.pciCompliant')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('pay.security.pciCompliantDesc')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* QR Code for Desktop to Mobile */}
            {!isMobile && !embedMode && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    {t('pay.continueOnMobile')}
                  </CardTitle>
                  <CardDescription>
                    {t('pay.continueOnMobileDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-white p-4 rounded-lg">
                    <QRCode 
                      value={window.location.href}
                      size={200}
                      style={{ width: "100%", height: "auto", maxWidth: "200px", margin: "0 auto", display: "block" }}
                      level="M"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    {t('pay.usePhoneCamera')}
                  </p>
                </CardContent>
              </Card>
            )}

            {!user && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{t('pay.guestCheckout.title')}</strong><br />
                  {t('pay.guestCheckout.description')}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPage;
